import { useEffect, useLayoutEffect, useRef, useState } from "react";

// Presentation-only scroll state for the ACTIVE execution identity (§15.4
// chrome). It never touches the execution: no revision, activity event,
// IndexedDB write, sync or request.
//
// Hysteresis keeps the header from flickering. Compacting shortens the sticky
// header, so the page can be pulled back by that difference (browser scroll
// anchoring, or the clamp at the end of a short page); expanding pushes it
// forward again. The layout change is always smaller than the expanded
// identity itself, so the identity compacts only once the page has scrolled
// past the exit threshold plus the expanded identity height (and never before
// IDENTITY_COMPACT_ENTER_Y). Neither layout change can then cross the other
// threshold by itself, whatever the title length or text size.
export const IDENTITY_COMPACT_ENTER_Y = 72;
export const IDENTITY_COMPACT_EXIT_Y = 16;

export const IDENTITY_PHASE = Object.freeze({
  // Expanded, never compacted yet: no transition on first paint.
  TOP: "top",
  COMPACT: "compact",
  // Expanded again after a compact phase.
  EXPANDED: "expanded",
});

export function identityCompactEnterY(expandedHeight = 0) {
  const height = Number(expandedHeight);
  return Math.max(
    IDENTITY_COMPACT_ENTER_Y,
    IDENTITY_COMPACT_EXIT_Y + (Number.isFinite(height) && height > 0 ? height : 0)
  );
}

export function nextIdentityCompact(compact, scrollY, expandedHeight = 0) {
  if (!Number.isFinite(scrollY)) return compact;
  return compact
    ? scrollY > IDENTITY_COMPACT_EXIT_Y
    : scrollY >= identityCompactEnterY(expandedHeight);
}

function readScrollY(windowObject) {
  const value = windowObject.scrollY ?? windowObject.pageYOffset;
  return Number(value) || 0;
}

export default function useCompactIdentityOnScroll({
  identityRef = null,
  windowObject = typeof window === "undefined" ? null : window,
  // Presentation hook: called synchronously just before the phase flips, while
  // the old layout is still on screen (used to animate from it).
  onBeforeChange = null,
} = {}) {
  const [phase, setPhase] = useState(IDENTITY_PHASE.TOP);
  const compactRef = useRef(false);
  const beforeChangeRef = useRef(onBeforeChange);
  beforeChangeRef.current = onBeforeChange;

  useEffect(() => {
    if (!windowObject?.addEventListener) return undefined;
    // At most one evaluation per frame, however many scroll events arrive.
    let pending = false;
    let frame = null;

    // State changes only when the boolean flips, never per scrolled pixel.
    function evaluate() {
      pending = false;
      frame = null;
      const expandedHeight = compactRef.current
        ? 0
        : identityRef?.current?.getBoundingClientRect?.().height || 0;
      const next = nextIdentityCompact(
        compactRef.current,
        readScrollY(windowObject),
        expandedHeight
      );
      if (next === compactRef.current) return;
      beforeChangeRef.current?.();
      compactRef.current = next;
      setPhase(next ? IDENTITY_PHASE.COMPACT : IDENTITY_PHASE.EXPANDED);
    }

    function handleScroll() {
      if (pending) return;
      pending = true;
      if (typeof windowObject.requestAnimationFrame !== "function") {
        evaluate();
        return;
      }
      const id = windowObject.requestAnimationFrame(evaluate);
      if (pending) frame = id;
    }

    // A restored scroll position (Back into the workout) applies at once.
    evaluate();
    windowObject.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      windowObject.removeEventListener("scroll", handleScroll);
      if (frame != null) windowObject.cancelAnimationFrame?.(frame);
    };
  }, [identityRef, windowObject]);

  return phase;
}

// Compact row priority: title, then sync status, then classification.
// FULL keeps all three; TITLE omits the classification from the visual row (it
// stays in the expanded header and in the accessible tree); TITLE_TIGHT also
// sets the title one step smaller (CSS 0.9rem instead of 1rem) when it cannot
// fit beside the chip at the compact size. Ellipsis is only the last resort.
export const COMPACT_ROW = Object.freeze({
  FULL: "full",
  TITLE: "title",
  TITLE_TIGHT: "title-tight",
});
// Must match the CSS ratio of the tight title (0.9rem) to the compact one (1rem).
export const COMPACT_TITLE_TIGHT_SCALE = 0.9;
// Leaving TITLE_TIGHT needs this much spare room, so sub-pixel rounding of the
// rescaled title width can never bounce between the two sizes.
const TIGHT_RELEASE_PX = 2;

// `title` is the title's natural width at the compact (1rem) size.
export function compactRowLayout(
  { available, title, context, chip, gap = 0 },
  current = COMPACT_ROW.FULL
) {
  const values = [available, title, context, chip, gap];
  if (!values.every(Number.isFinite) || available <= 0) return COMPACT_ROW.FULL;
  const room = available - (current === COMPACT_ROW.TITLE_TIGHT ? TIGHT_RELEASE_PX : 0);
  if (title + context + chip + 2 * gap <= room) return COMPACT_ROW.FULL;
  if (title + chip + gap <= room) return COMPACT_ROW.TITLE;
  return COMPACT_ROW.TITLE_TIGHT;
}

function measureCompactRow(identity, current) {
  const title = identity.querySelector("h1");
  const context = identity.querySelector(".lz-workout-execution-chrome__classification");
  const chip = identity.querySelector(".lz-workout-sync-chip");
  if (!title || !context || !chip) return COMPACT_ROW.FULL;
  const style = identity.ownerDocument.defaultView.getComputedStyle(identity);
  const titleScale = current === COMPACT_ROW.TITLE_TIGHT ? COMPACT_TITLE_TIGHT_SCALE : 1;
  return compactRowLayout({
    available: identity.clientWidth -
      (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0),
    // scrollWidth keeps the natural single-line width whether the element is
    // clipped, ellipsized or visually hidden, so the answer never depends on
    // the layout it picks (the tight title is rescaled to the compact size).
    title: title.scrollWidth / titleScale,
    context: context.scrollWidth,
    chip: chip.getBoundingClientRect().width,
    gap: parseFloat(style.columnGap) || 0,
  }, current);
}

// Measures only while compact, before paint, then only on real size changes
// (row width, a new chip label, a new title). State changes only when the
// layout changes. Without ResizeObserver the row keeps all three (the
// classification then ellipsizes).
export function useCompactRowLayout(identityRef, active, {
  ResizeObserverImpl = typeof ResizeObserver === "undefined" ? null : ResizeObserver,
} = {}) {
  const [layout, setLayout] = useState(COMPACT_ROW.FULL);
  const layoutRef = useRef(COMPACT_ROW.FULL);

  useLayoutEffect(() => {
    const identity = identityRef?.current;
    if (!active || !identity || typeof ResizeObserverImpl !== "function") return undefined;

    function update() {
      const next = measureCompactRow(identity, layoutRef.current);
      if (next === layoutRef.current) return;
      layoutRef.current = next;
      setLayout(next);
    }

    const observer = new ResizeObserverImpl(update);
    [identity, ...identity.children].forEach((element) => observer.observe(element));
    update();
    return () => observer.disconnect();
  }, [active, identityRef, ResizeObserverImpl]);

  return active ? layout : COMPACT_ROW.FULL;
}
